import "mapbox-gl/dist/mapbox-gl.css";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { forEach, findIndex, find, filter, isEmpty } from "lodash";
import { Modal, Button } from "antd";
import { useSelector } from "react-redux";
import socketIOClient from "socket.io-client";
import { ReactSVG } from "react-svg";
import * as turf from "@turf/turf";

import DefaultMapbox from "./DefaultMapbox";
import markerIcons from "../Markers/markerIcons";
import { COLOR_DEFAULT } from "../../constants";

import {
  // PopupStopUnassigned,
  PopupRoute,
  // PopupStop,
  ContentStop,
  ContentRoute,
} from "./Popup";
import { addPolylines, getStopLeft } from "./Utils";
import { getDriverIds } from "../RouteChart/Utils";

import {
  Editor,
  EditingMode,
  DrawLineStringMode,
  // DrawPolygonMode,
} from "react-map-gl-draw";
import DuplicateRouteForm from "../Forms/Route/DuplicateRouteForm";

const socketURI = process.env.REACT_APP_SOCKET_URI || "";
const socket = socketIOClient(socketURI);

const StopsMapbox = ({
  routes: baseRoutes,
  stops,
  stopClicked,
  onClickStop,
  routeClicked,
  onClickRoute,
  onClosePopup,
  clicked = true,
  setClicked,
  driverIdsHide = [],
  routeType = null,
  setRouteType,
  showMoreRoute = false,
  setShowMoreRoute,
  orders,
  showToolbarDraw = false,
  setIsBulkUpdateStopsSelected,
  selectedStops,
  setSelectedStops,
  doLockDriver,
  routePlan,
  driverIdsLock,
  setDriverIdsLock,
  selectedDrivers = [],
  showStopContent,
  setShowStopContent,
  stepClickedParams,
  defaultCenter = undefined,
}) => {
  const defaultColor = COLOR_DEFAULT;
  const defaultFeature = {
    type: "Feature",
    properties: {
      guideType: "tentative",
    },
    geometry: {
      type: "LineString",
      coordinates: [],
    },
  };
  const [showRouteContent, setShowRouteContent] = useState(undefined);
  const [moveToPoint, setMoveToPoint] = useState(undefined);
  const [liveDrivers, setLiveDrivers] = useState({});

  const [modeHandler, setModeHandler] = useState();
  const [features, setFeatures] = useState([defaultFeature]);
  const [drawCoordinates, setDrawCoordinates] = useState([]);
  const editorRef = useRef(null);
  const featureRef = useRef(null);

  const { user } = useSelector(state => state.auth);
  const canEdit = !user?.roles?.includes("user");
  const setting = user?.company || {};

  const clickRoute = () => {
    const content = getContentRoute();
    setShowRouteContent(content);
    setTimeout(() => {
      setShowMoreRoute && setShowMoreRoute(false);
    }, 1000);
  };

  const clickShowMoreStop = stop => {
    const content = getContentStop(stop);
    setShowStopContent(content);
  };

  // const getPopupStopUnassigned = (stop) => {
  //   return (
  //     <PopupStopUnassigned
  //       key={stop.id}
  //       stop={stop}
  //       onClosePopup={onClosePopup}
  //       clickShowMoreStop={clickShowMoreStop}
  //       setting={setting}
  //     />
  //   );
  // };

  const getPopupRoute = type => {
    return (
      <PopupRoute
        key={`${routeClicked.driver?._id}-${type}`}
        routeClicked={routeClicked}
        onClosePopup={onClosePopup}
        clickRoute={clickRoute}
        setting={setting}
        type={type}
      />
    );
  };

  // const getPopupStop = (stopClicked) => {
  //   return (
  //     <PopupStop
  //       key={stopClicked.id}
  //       stopClicked={stopClicked}
  //       onClosePopup={onClosePopup}
  //       getContentStop={getContentStop}
  //     />
  //   );
  // };

  const getContentStop = stop => {
    const order = orders.find(o => o._id === stopClicked.orderId);
    return (
      <ContentStop
        stop={stop}
        onClickStop={onClickStop}
        getStopLeft={getStopLeft}
        unassignedStops={unassignedStops}
        clickShowMoreStop={clickShowMoreStop}
        stepClicked={stepClicked}
        stops={stops}
        setting={setting}
        order={order}
      />
    );
  };

  const getContentRoute = () => {
    return (
      <ContentRoute
        routeClicked={routeClicked}
        onClickStop={onClickStop}
        routes={routes}
        steps={steps}
        canEdit={canEdit}
        setting={setting}
        onDuplicateDriver={handleDuplicateDriver}
      />
    );
  };

  const newRoutes = addPolylines(baseRoutes);

  let steps = [];
  const routes = {};
  const routesList = [];
  let handledOrderIds = [];

  forEach(newRoutes, route => {
    const driver = route.driver;

    steps = steps.concat(route.steps);
    routes[driver._id] = [];

    if (driverIdsHide.includes(driver._id)) return;

    routesList.push(route);

    // Add handled steps
    const stepOrders = route.steps.map(s => s.orderId);
    let handledSteps = route.handledSteps ?? [];
    handledSteps = handledSteps.filter(step => {
      return step && !stepOrders.includes(step.orderId);
    });
    const handledStepIds = [];
    handledSteps.forEach(s => {
      const stop = find(stops, s1 => s1.orderId === s.orderId && s1.type === s.type.toLowerCase());
      if (stop) {
        routes[driver._id].push(stop);
        handledStepIds.push(s.orderId);
      }
      steps.push(s);
    });
    handledOrderIds = handledOrderIds.concat(handledStepIds);

    forEach(route.steps, s => {
      const stop = find(stops, s1 => s1.orderId === s.orderId && s1.type === s.type.toLowerCase());
      if (stop) {
        routes[driver._id].push(stop);
      }
    });
  });

  // move routeClicked to end
  if (routeClicked) {
    const routeClickedIndex = findIndex(routesList, r => r.driver?._id === routeClicked.driver?._id);
    const [removed] = routesList.splice(routeClickedIndex, 1);
    routesList.splice(routesList?.length, 0, removed);
  }

  //
  const orderIds = steps.map(s1 => s1.orderId);
  const unassignedStops = filter(stops, s => !orderIds.includes(s?.orderId) && !handledOrderIds.includes(s?.orderId));
  const stepClicked = find(
    steps,
    s => stopClicked?.orderId === s.orderId && stopClicked?.type === s.type.toLowerCase(),
  );

  /** RENDER **/
  const polylines = [];
  const markers = [];
  const contents = [];

  const stopUnassignedLeft = getStopLeft(stopClicked, unassignedStops);

  // Markers for unassigned stops
  forEach(unassignedStops, (stop, index) => {
    if (driverIdsHide.includes("unassigned")) return;
    const stopOrder = index + 1;
    const markerColor = "606060";
    const urlStop = markerIcons[stop.type][markerColor];
    const zIndexStop = stopClicked?.id === stop.id || stopUnassignedLeft?.id === stop.id ? 999 : stopOrder;
    const markerStop = (
      <div
        key={`${stop.id}`}
        className="marker"
        onClick={() => {
          setClicked && setClicked(true);
          onClickStop(stop);
        }}
        style={{ zIndex: zIndexStop }}>
        <img src={urlStop} style={{ width: 24, height: 29 }} alt="" />
        <div
          className="marker__info"
          style={{
            color: `#${markerColor}`,
            fontSize: "10px",
            fontWeight: "bold",
          }}>
          {["failed", "skipped", "completed"].includes(stop.status) ? " " : stopOrder.toString()}
        </div>
      </div>
    );

    markers.push({
      content: markerStop,
      latitude: stop.lat,
      longitude: stop.lng,
      offsetLeft: -12,
      offsetTop: -29,
      className: zIndexStop === 999 ? "active-pin" : "",
    });
  });

  // Markers for routes
  forEach(routesList, (route, index) => {
    if (!route) {
      return;
    }
    const driver = route.driver;
    const liveDriver = liveDrivers[driver._id];
    // For marker drivers
    const driverIndex = index + 1;
    const driverColor = driver.color || defaultColor;
    const urlStart = markerIcons["start"][driverColor];
    const zIndexDriver = routeClicked?.driver?._id === driver._id ? 999 : 999 - driverIndex;
    const markerStart = (
      <div
        className="marker--driver"
        onClick={() => {
          setClicked && setClicked(true);
          onClickRoute && onClickRoute(route);
          setRouteType && setRouteType("start");
        }}
        style={{ zIndex: zIndexDriver }}>
        <img src={urlStart} style={{ width: 34, height: 38 }} alt="" />
      </div>
    );
    if (liveDriver) {
      const urlLiveDriver = markerIcons["liveDriver"][driverColor];
      const markerDriver = (
        <div className="marker--driver" style={{ zIndex: 9999 }}>
          <img src={urlLiveDriver} style={{ width: 34, height: 38 }} alt="" />
        </div>
      );
      markers.push({
        content: markerDriver,
        latitude: liveDriver.lat,
        longitude: liveDriver.lng,
        offsetLeft: -17,
        offsetTop: -17,
        className: "active-pin",
      });
    }

    markers.push({
      content: markerStart,
      latitude: route.startLat || route.driver.startLat,
      longitude: route.startLng || route.driver.startLng,
      offsetLeft: -17,
      offsetTop: -17,
      className: zIndexDriver === 999 ? "active-pin" : "",
    });

    if (route.endLat && route.endLng) {
      const urlEnd = markerIcons["end"][driverColor];
      const markerEnd = (
        <div
          className="marker--driver"
          onClick={() => {
            setClicked && setClicked(true);
            onClickRoute && onClickRoute(route);
            setRouteType && setRouteType("end");
          }}
          style={{ zIndex: zIndexDriver }}>
          <img src={urlEnd} style={{ width: 34, height: 38 }} alt="" />
        </div>
      );
      markers.push({
        content: markerEnd,
        latitude: route.endLat,
        longitude: route.endLng,
        offsetLeft: -17,
        offsetTop: -38,
        className: zIndexDriver === 999 ? "active-pin" : "",
      });
    }
    // For polyline
    const polyline = route.polyline;
    const polylineColor = driver.color || defaultColor;
    if (polyline) {
      polylines.push({
        points: route.pathCoordinate,
        strokeColor: `#${polylineColor}`,
        strokeWeight: 2,
        strokeOpacity: 0.8,
      });
    }
    const stopsFilter = routes[route.driver._id].filter(s => s.status !== "completed");
    forEach(stopsFilter, (stop, stopIndex) => {
      const stopOrder = stopIndex + 1;
      const conpleted =
        (stop.status === "COMPLETED" && stop.type === "delivery") ||
        (["IN_TRANSIT", "DELIVERY_ARRIVED", "DELIVERY_FAILED", "COMPLETED"].includes(stop.status) &&
          stop.type === "pickup");
      const urlStop = markerIcons[`${stop.type}${conpleted ? "End" : ""}`][driverColor];
      const zIndexStop = stopClicked?.id === stop.id ? 999 : 999 - stopOrder;
      const markerStop = (
        <div
          key={`${stop.orderId}-${stop.type}`}
          className="marker"
          onClick={() => {
            setClicked && setClicked(true);
            onClickStop(stop);
          }}
          style={{ zIndex: zIndexStop }}>
          <img src={urlStop} style={{ width: 30, height: 31 }} alt="" />
          <div
            className="marker__info"
            style={{
              color: conpleted
                ? "#fff"
                : `#${
                    stepClickedParams?.orderId === stop.orderId && stepClickedParams?.type === stop.type
                      ? "000000"
                      : driverColor
                  }`,
              fontSize: "11px",
              fontWeight: "bold",
              letterSpacing: -1,
              paddingRight: 1,
            }}>
            {["failed", "skipped", "completed"].includes(stop.status) ? " " : stopOrder.toString()}
          </div>
        </div>
      );

      markers.push({
        content: markerStop,
        latitude: stop.lat,
        longitude: stop.lng,
        offsetLeft: -15,
        offsetTop: -23,
        className: zIndexStop === 999 ? "active-pin" : "",
      });
    });
  });

  // Markers for selelectedDrivers
  forEach(selectedDrivers, (driver, index) => {
    // For marker drivers
    const driverIndex = index + 1;
    const driverColor = driver.color || defaultColor;
    const urlStart = markerIcons["start"][driverColor];
    const zIndexDriver = routeClicked?.driver?._id === driver._id ? 999 : 999 - driverIndex;
    const markerStart = (
      <div className="marker--driver" style={{ zIndex: zIndexDriver }}>
        <img src={urlStart} style={{ width: 34, height: 38 }} alt="" />
      </div>
    );

    markers.push({
      content: markerStart,
      latitude: driver.startLat,
      longitude: driver.startLng,
      offsetLeft: -17,
      offsetTop: -17,
      className: zIndexDriver === 999 ? "active-pin" : "",
    });
  });

  // Popup information
  // if (stopClicked?.lat) {
  //   const stopUnassignedIndex = findIndex(
  //     unassignedStops,
  //     (s) => s.id === stopClicked?.id
  //   );
  //   // Show only address for unassigned stop
  //   if (stopUnassignedIndex >= 0) {
  //     const order = orders.find((o) => stopClicked.orderCode === o.orderCode);
  //     const s = {
  //       ...stopClicked,
  //       unscheduledReason: order?.unscheduledReason,
  //     };
  //     const stopLeft = getStopLeft(s, unassignedStops);
  //     if (stopLeft) {
  //       const popupStopLeft = getPopupStopUnassigned(stopLeft);
  //       contents.push(popupStopLeft);
  //     }
  //     const popupStop = getPopupStopUnassigned(s);
  //     contents.push(popupStop);
  //   } else {
  //     const popup = getPopupStop(stopClicked);
  //     contents.push(popup);
  //   }
  // }

  if (routeClicked?.endLat && (routeType === "end" || routeType === null)) {
    const popupRoute = getPopupRoute("end");
    contents.push(popupRoute);
  }
  if (routeClicked?.startLat && (routeType === "start" || routeType === null)) {
    const popupRoute = getPopupRoute("start");
    contents.push(popupRoute);
  }

  // Move map to point
  useMemo(() => {
    if (routeClicked && clicked) {
      if (routeType === "end" && routeClicked?.endLat) {
        setMoveToPoint({
          lat: routeClicked?.endLat,
          lng: routeClicked?.endLng,
        });
      } else if (routeClicked?.startLat) {
        setMoveToPoint({
          lat: routeClicked?.startLat,
          lng: routeClicked?.startLng,
        });
      }
    }
  }, [routeClicked, clicked]);

  useMemo(() => {
    if (stopClicked?.lat && clicked) {
      setMoveToPoint({ lat: stopClicked.lat, lng: stopClicked.lng });
    }
  }, [stopClicked, clicked]);

  useEffect(() => {
    if (showRouteContent) {
      // clickRoute();
    }
  }, [stops]);

  useEffect(() => {
    onClickStop({ id: "closed" });
  }, []);

  useEffect(() => {
    if (showMoreRoute) {
      clickRoute();
    }
  }, [showMoreRoute]);

  useEffect(() => {
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    forEach(baseRoutes, route => {
      if (!route) {
        return;
      }
      const driverId = route.driver._id;
      socket.off(driverId).on(driverId, data => {
        if (data.event === "driver.changing.location") {
          const liveDriversT = { ...liveDrivers };
          liveDriversT[driverId] = { ...data.payload };
          setLiveDrivers(liveDriversT);
        }
      });
    });
  }, [baseRoutes]);

  const checkOrderInsideHideRoute = stop => {
    if (driverIdsHide.includes("unassigned")) {
      return stop.status != "UNSCHEDULED";
    } else {
      const routeForStop = baseRoutes.find(route => route.orders.includes(stop.orderId));
      return !driverIdsHide.includes(routeForStop?.driver?._id);
    }
  };

  useEffect(() => {
    if (!isEmpty(drawCoordinates)) {
      const stopsWithin = [];
      stops.forEach(stop => {
        if (checkOrderInsideHideRoute(stop)) {
          const point = turf.point([stop.lng, stop.lat]);
          const polygon = turf.polygon([drawCoordinates]);
          const ptsWithin = turf.booleanPointInPolygon(point, polygon);
          if (ptsWithin) {
            stopsWithin.push(stop);
          }
        }
      });
      setSelectedStops(stopsWithin);
    }
  }, [drawCoordinates, driverIdsHide]);

  const handleOnSelectEditor = e => {
    if (e.selectedEditHandleIndex === null) {
      if (e.mapCoords) {
        const lineStringFeature = { ...defaultFeature, ...features[0] };
        lineStringFeature.geometry.coordinates.push(e.mapCoords);
        featureRef.current = { ...lineStringFeature };
        setFeatures([lineStringFeature]);
      } else {
        featureRef.current = null;
        setFeatures([]);
      }
    } else if (e.selectedEditHandleIndex === 0 && !isEmpty(featureRef.current)) {
      const polygonFeature = { ...featureRef.current };
      if (polygonFeature.geometry.coordinates.length > 2) {
        const polygonCoordinates = [];
        polygonCoordinates[0] = polygonFeature.geometry.coordinates;
        polygonCoordinates[0].push(polygonFeature.geometry.coordinates[e.selectedEditHandleIndex]);
        polygonFeature.geometry.type = "Polygon";
        polygonFeature.geometry.coordinates = polygonCoordinates;
        setFeatures([polygonFeature]);
        setDrawCoordinates(polygonFeature.geometry.coordinates[0]);
        setModeHandler(new EditingMode());
      }
    }
  };

  const handleChangeToDraw = value => {
    if (value) {
      const driverIds = getDriverIds(baseRoutes, driverIdsLock, "all-drivers");
      if (driverIds.length) {
        setDriverIdsLock(driverIds);
        doLockDriver({
          id: routePlan._id,
          driver: undefined,
          locked: true,
        });
      }
      setModeHandler(new DrawLineStringMode());
    } else {
      setModeHandler();
      setFeatures([]);
      setDrawCoordinates([]);
      setSelectedStops([]);
      setIsBulkUpdateStopsSelected(false);
    }
  };

  const handleDeleteStopsSelected = () => {
    setFeatures([]);
    setDrawCoordinates([]);
    setSelectedStops([]);
    setIsBulkUpdateStopsSelected(false);
    setModeHandler(new DrawLineStringMode());
  };

  const handleBulkUpdateStopsSelected = () => {
    setIsBulkUpdateStopsSelected(true);
  };

  const keyDownHandler = event => {
    if (event.key === "Enter" && drawCoordinates.length === 0) {
      handleOnSelectEditor({ selectedEditHandleIndex: 0 });
    }
    if (event.key === "Escape") {
      handleDeleteStopsSelected();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  const renderToolbarDraw = () => {
    return (
      <div className="ToolbarDraw">
        <div className="mapboxgl-ctrl-group">
          <button
            className={`mapbox-ctrl-draw-btn ${isEmpty(modeHandler) ? "active" : ""}`}
            onClick={() => handleChangeToDraw(false)}>
            <ReactSVG className="icon" src="/icons/pan_tool.svg" />
          </button>
          <button
            className={`mapbox-ctrl-draw-btn ${!isEmpty(modeHandler) ? "active" : ""}`}
            onClick={() => handleChangeToDraw(true)}>
            <ReactSVG className="icon" src="/icons/polyline.svg" />
          </button>
        </div>
        {!isEmpty(modeHandler) && (
          <div className="tips">
            <div className="title">
              <b>Tips:</b> While drawing the polyline, you can
            </div>
            <div className="items">
              <div className="item">
                1. Press <b>Enter</b> to complete the polyline
              </div>
              <div className="item">
                2. Press <b>Esc</b> to cancel the polyline
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderToolbarActions = () => {
    return (
      <div className="ToolbarActions">
        <Button className="ant-btn-close">
          <b>{selectedStops.length}&nbsp;</b>
          <span>stops selected</span>
          <ReactSVG className="icon" src="/icons/close.svg" onClick={() => handleDeleteStopsSelected()} />
        </Button>
        {selectedStops.length > 0 && (
          <Button className="ant-btn-bulk-update" onClick={handleBulkUpdateStopsSelected}>
            <b>Bulk Update</b>
            <ReactSVG className="icon" src="/icons/bulk-update.svg" />
          </Button>
        )}
      </div>
    );
  };

  const [duplicateRouteState, setDuplicateRouteState] = useState({
    isShow: false,
    driver: null,
  });

  const handleDuplicateDriver = async driver => {
    setDuplicateRouteState({
      ...duplicateRouteState,
      isShow: true,
      driver,
    });
  };

  const closeDuplicateDriver = async () => {
    setDuplicateRouteState({
      ...duplicateRouteState,
      isShow: false,
      driver: null,
    });
  };

  return (
    <>
      <DefaultMapbox
        markers={markers}
        polylines={polylines}
        moveToPoint={moveToPoint}
        defaultCenter={defaultCenter}
        resetMoveToPoint={() => setMoveToPoint(undefined)}
        className={`${!isEmpty(modeHandler) && "Draw"} ${drawCoordinates.length > 0 && "DrawCompleted"}`}>
        {contents}
        {showToolbarDraw && (
          <>
            {!isEmpty(modeHandler) && (
              <>
                <Editor
                  ref={editorRef}
                  clickRadius={0}
                  mode={modeHandler}
                  onSelect={handleOnSelectEditor}
                  features={features}
                  selectedFeatureIndex={0}
                  featureStyle={() => {
                    return {
                      stroke: "var(--primary-color)",
                      strokeWidth: "2",
                      fill: "var(--primary-color)",
                      fillOpacity: "0.12",
                    };
                  }}
                  editHandleStyle={() => {
                    return {
                      stroke: "var(--primary-color)",
                      strokeWidth: "2",
                      fill: "rgb(255 255 255)",
                      r: "4",
                    };
                  }}
                  editHandleShape={"circle"}
                  className="DrawEditor"
                />
                {renderToolbarActions()}
              </>
            )}
            {renderToolbarDraw()}
          </>
        )}
      </DefaultMapbox>
      <Modal
        centered
        open={!!showRouteContent}
        onCancel={() => setShowRouteContent(undefined)}
        width={1320}
        footer={null}
        zIndex={1200}
        className="showRouteContent">
        {showRouteContent}
      </Modal>
      <Modal
        centered
        open={!!showStopContent}
        onCancel={() => setShowStopContent(undefined)}
        width={500}
        footer={null}
        zIndex={1200}
        className="showStopContent">
        {showStopContent}
      </Modal>

      <DuplicateRouteForm
        routePlan={routePlan}
        driver={duplicateRouteState.driver}
        isShow={duplicateRouteState.isShow}
        onClose={closeDuplicateDriver}
      />
    </>
  );
};

export default StopsMapbox;
